import { useQuery } from '@tanstack/react-query';
import { getAssetUSDPrice } from '@/onchain/hooks/useAssetUSDPrice';

/**
 * Retrieves the USD price of a token from the Coinbase API.
 * @param token - The token symbol.
 * @param enabled - Optional flag to conditionally enable/disable fetching
 * @returns USD price of the token.
 */
const useTokenPrice = (token: string, enabled = true) => {
  const {
    isLoading,
    isError,
    data: price,
    error,
  } = useQuery<number>({
    queryKey: ['tokenPrice', token],
    queryFn: async () => getAssetUSDPrice(token),
    enabled: !!token && enabled, // Only fetch if we have a token and enabled is true
  });
  return { isLoading, isError, price, error };
};

const useTokenPrices = (tokens: string[], enabled = true) => {
  const { data: tokenPrices = {}, ...rest } = useQuery({
    queryKey: ['tokenPrices', ...tokens.sort()],
    queryFn: async () => {
      const prices = await Promise.all(tokens.map(async (token) => getAssetUSDPrice(token)));
      return tokens.reduce(
        (acc, token, i) => {
          acc[token] = prices[i];
          return acc;
        },
        {} as Record<string, number>,
      );
    },
    enabled: !!tokens.length && enabled, // Only fetch if we have tokens and enabled is true
  });
  return { ...rest, tokenPrices };
};

export { useTokenPrice, useTokenPrices };
